import React from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  Text,
  View,
} from "react-native";

import type { TCommunicationsPhoneNumberPricing } from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { PrimaryButton } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import type { TCommunicationsUiState } from "../types";

type CommunicationsSetupCardProps = {
  isConfigTab: boolean;
  communicationsUiState: TCommunicationsUiState;
  communicationsPricingLoading: boolean;
  communicationsPricingError: string | null;
  communicationsPricing: TCommunicationsPhoneNumberPricing | null;
  communicationsIsActivated: boolean;
  activationSubmitting: boolean;
  availableNumbersLoading: boolean;
  availablePhoneNumbersCount: number;
  availableNumbersError: string | null;
  onActivateCommunications: () => void;
  formatCurrencyFromCents: (
    amountCents: number,
    currencyCode?: string,
  ) => string;
  formatInteger: (value?: number | null) => string;
};

const CommunicationsSetupCard: React.FC<CommunicationsSetupCardProps> = ({
  isConfigTab,
  communicationsUiState,
  communicationsPricingLoading,
  communicationsPricingError,
  communicationsPricing,
  communicationsIsActivated,
  activationSubmitting,
  availableNumbersLoading,
  availablePhoneNumbersCount,
  availableNumbersError,
  onActivateCommunications,
  formatCurrencyFromCents,
  formatInteger,
}) => {
  return (
    <View style={[styles.card, !isConfigTab && styles.hiddenSection]}>
      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>Communications Setup</Text>
        <Text
          style={[
            styles.modeLabel,
            communicationsUiState === "communications_not_initialized" &&
              styles.modeLabelMuted,
          ]}
        >
          {communicationsUiState === "communications_activating"
            ? "Activating"
            : communicationsUiState === "communications_not_initialized"
              ? "Not Active"
              : "Active"}
        </Text>
      </View>

      {communicationsPricingLoading ? (
        <View style={styles.loadingInlineRow}>
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading package details...</Text>
        </View>
      ) : communicationsPricingError ? (
        <Text style={styles.inlineErrorText}>{communicationsPricingError}</Text>
      ) : communicationsPricing ? (
        <View style={styles.packageSummaryList}>
          <Text style={styles.metaText}>
            Base add-on:{" "}
            {formatCurrencyFromCents(
              communicationsPricing.monthly_price_cents,
              communicationsPricing.currency,
            )}
            /month
          </Text>
          <Text style={styles.metaText}>
            Included numbers:{" "}
            {formatInteger(communicationsPricing.included_number_count)}
          </Text>
          <Text style={styles.metaText}>
            Included minutes:{" "}
            {formatInteger(communicationsPricing.included_minutes)}
          </Text>
          <Text style={styles.metaText}>
            Extra number:{" "}
            {formatCurrencyFromCents(
              communicationsPricing.extra_number_monthly_price_cents,
              communicationsPricing.currency,
            )}
            /month
          </Text>
          <Text style={styles.metaText}>
            Overage:{" "}
            {formatCurrencyFromCents(
              communicationsPricing.overage_minute_price_cents,
              communicationsPricing.currency,
            )}
            /minute
          </Text>
        </View>
      ) : null}

      {!communicationsIsActivated ? (
        <View style={styles.sectionStack}>
          <Text style={styles.ftuiHeadline}>
            Turn on a clinic-owned line to reduce missed calls and keep caller
            identity consistent.
          </Text>
          <Text style={styles.metaText}>
            Includes one phone number and tracked minutes so billing and usage
            stay transparent.
          </Text>
          <View style={styles.ftuiValueBulletList}>
            <Text style={styles.metaText}>
              • Standardized outbound caller ID for staff
            </Text>
            <Text style={styles.metaText}>
              • First number is assigned during setup
            </Text>
            <Text style={styles.metaText}>
              • Usage and overage are visible in this page
            </Text>
          </View>
          <PrimaryButton
            text="Set Up Communications"
            onPress={onActivateCommunications}
            loading={activationSubmitting}
            disabled={
              activationSubmitting ||
              availableNumbersLoading ||
              availablePhoneNumbersCount <= 0
            }
            fullWidth
          />
          {availableNumbersError ? (
            <Text style={styles.inlineErrorText}>{availableNumbersError}</Text>
          ) : null}
          {!availableNumbersLoading && availablePhoneNumbersCount <= 0 ? (
            <Text style={styles.inlineErrorText}>
              No numbers are currently available for activation.
            </Text>
          ) : null}
          <View style={styles.setupStepsCard}>
            <Text style={styles.callNumberText}>
              What happens after activation
            </Text>
            <Text style={styles.metaText}>
              1. Communications billing add-on starts.
            </Text>
            <Text style={styles.metaText}>
              2. Your first number is automatically assigned.
            </Text>
            <Text style={styles.metaText}>
              3. That number appears in the dialer&apos;s From list.
            </Text>
            <Text style={styles.metaText}>
              Billing starts when the first number is assigned. Overage applies
              only above included minutes.
            </Text>
          </View>
          <Pressable
            onPress={() =>
              Linking.openURL("mailto:support@clinicos.ai").catch(
                () => undefined,
              )
            }
            accessibilityLabel="Open communications pricing support"
          >
            <Text style={styles.linkText}>
              Need more detail? Contact support for pricing FAQ.
            </Text>
          </Pressable>
        </View>
      ) : (
        <Text style={styles.metaText}>
          Communications is active. Manage assigned numbers below.
        </Text>
      )}
    </View>
  );
};

export default CommunicationsSetupCard;
